import { useEffect, useState, useRef, useCallback } from 'react';
import { extractEpub } from '../lib/epub';
import * as shelf from '../lib/bookshelf';

/**
 * useAudiobook — turn a PDF into a spoken audiobook.
 *
 * Extracts the text of a PDF entirely in the browser (via pdf.js loaded from a
 * CDN) and reads it aloud sentence-by-sentence through the same local Kokoro
 * TTS server the voice assistant uses. Playback is a self-contained queue with
 * play / pause / resume / stop and page + progress tracking, kept separate from
 * the conversation state machine so reading a book never touches the mic.
 */

const KOKORO_URL = 'https://tts.buildabetterweb.site';

// pdf.js UMD build (exposes window.pdfjsLib) + its web worker.
const PDFJS_VERSION = '3.11.174';
const PDFJS_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

// iOS forces getUserMedia audio through the earpiece, so play WAV bytes through
// an HTMLAudioElement (speaker route) instead of the AudioContext.
const IS_IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

let pdfJsPromise = null;
/** Load pdf.js once (cached) and return window.pdfjsLib. */
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsPromise) return pdfJsPromise;
  pdfJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_SRC;
    script.onload = () => {
      if (!window.pdfjsLib) return reject(new Error('pdf.js failed to load'));
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Could not load PDF reader'));
    document.head.appendChild(script);
  });
  return pdfJsPromise;
}

/** Clean a raw PDF text run for more natural speech (mirrors useKokoro). */
function preprocessForTTS(text) {
  return text
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\betc\./gi, 'etcetera')
    .replace(/\be\.g\./gi, 'for example')
    .replace(/\bi\.e\./gi, 'that is')
    .replace(/\bMr\./gi, 'Mister')
    .replace(/\bMrs\./gi, 'Missus')
    .replace(/\bMs\./gi, 'Miss')
    .replace(/\bDr\./gi, 'Doctor')
    .replace(/\bSt\./gi, 'Saint')
    // De-hyphenate words split across line breaks in the PDF ("infor- mation")
    .replace(/(\w)-\s+(\w)/g, '$1$2')
    .replace(/https?:\/\/[^\s]+/g, 'link')
    .replace(/[—–]/g, ', ')
    .replace(/\.{2,}/g, '.')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Split cleaned text into speakable sentences (long ones broken on clauses). */
function splitIntoSentences(text) {
  const raw = text.split(/(?<=[.!?])\s+/);
  const sentences = [];
  for (const chunk of raw) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    if (trimmed.length > 200) {
      const subParts = trimmed.split(/(?<=[,;])\s+/);
      let current = '';
      for (const part of subParts) {
        if (current && (current + ' ' + part).length > 200) {
          sentences.push(current);
          current = part;
        } else {
          current = current ? current + ' ' + part : part;
        }
      }
      if (current) sentences.push(current);
    } else {
      sentences.push(trimmed);
    }
  }
  return sentences;
}

export default function useAudiobook() {
  // status: idle | loading | reading | paused | done | error
  const [status, setStatus] = useState('idle');
  const [bookTitle, setBookTitle] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1
  const [error, setError] = useState('');

  const audioCtxRef = useRef(null);
  const abortRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const audioElRef = useRef(null);
  const sentencesRef = useRef([]); // [{ text, page }]
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const runningRef = useRef(false);
  const prefetchRef = useRef(null); // { index, promise }
  const bookIdRef = useRef(null);
  const bookMetaRef = useRef({ title: '', author: '' });
  const chapterNamesRef = useRef([]);
  const [books, setBooks] = useState([]);

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  }, []);

  /** Synthesize one sentence → AudioBuffer (desktop) or WAV ArrayBuffer (iOS). */
  const synthesize = useCallback(async (sentence, signal) => {
    const res = await fetch(KOKORO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: sentence, voice: 'bm_daniel', speed: 1.0 }),
      signal,
    });
    if (!res.ok) throw new Error('Kokoro ' + res.status);
    const wavBuffer = await res.arrayBuffer();
    if (IS_IOS) return wavBuffer;
    return ensureAudioContext().decodeAudioData(wavBuffer);
  }, [ensureAudioContext]);

  /** Play one buffer to completion (or until stopped). */
  const playBuffer = useCallback((buffer) => new Promise((resolve) => {
    if (IS_IOS) {
      const blob = new Blob([buffer], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioElRef.current = audio;
      const done = () => { audioElRef.current = null; URL.revokeObjectURL(url); resolve(); };
      audio.onended = done;
      audio.onerror = done;
      audio.play().catch(done);
    } else {
      const ctx = ensureAudioContext();
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      sourceNodeRef.current = source;
      source.onended = () => { sourceNodeRef.current = null; resolve(); };
      source.start(0);
    }
  }), [ensureAudioContext]);

  /** Stop whatever audio is currently sounding, without tearing down state. */
  const silenceCurrent = useCallback(() => {
    if (sourceNodeRef.current) { try { sourceNodeRef.current.stop(); } catch {} sourceNodeRef.current = null; }
    if (audioElRef.current) { try { audioElRef.current.pause(); audioElRef.current.src = ''; } catch {} audioElRef.current = null; }
  }, []);

  /** The reading loop — walks sentencesRef from indexRef, prefetching one ahead. */
  const readLoop = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    const total = sentencesRef.current.length;

    while (indexRef.current < total) {
      if (abortRef.current?.signal?.aborted || pausedRef.current) break;
      const i = indexRef.current;
      const item = sentencesRef.current[i];
      setCurrentPage(item.page);

      let buffer;
      try {
        if (prefetchRef.current?.index === i) {
          buffer = await prefetchRef.current.promise;
        } else {
          buffer = await synthesize(item.text, abortRef.current.signal);
        }
      } catch (err) {
        if (err.name === 'AbortError') break;
        console.error('[Audiobook] Synthesis failed:', err);
        runningRef.current = false;
        setError('Narration server unavailable. Please try again.');
        setStatus('error');
        return;
      }
      if (abortRef.current?.signal?.aborted || pausedRef.current) break;

      // Kick off synthesis of the next sentence while this one plays.
      const nextI = i + 1;
      if (nextI < total) {
        prefetchRef.current = { index: nextI, promise: synthesize(sentencesRef.current[nextI].text, abortRef.current.signal).catch(() => null) };
      }

      await playBuffer(buffer);
      if (pausedRef.current || abortRef.current?.signal?.aborted) break;

      indexRef.current = i + 1;
      setProgress(indexRef.current / total);
    }

    runningRef.current = false;
    if (abortRef.current?.signal?.aborted) return;
    if (pausedRef.current) { setStatus('paused'); return; }
    if (indexRef.current >= total) {
      setProgress(1);
      setStatus('done');
    }
  }, [synthesize, playBuffer]);

  /** Load a PDF file, extract its text, and start reading it aloud. */
  const loadAndRead = useCallback(async (file, startIndex = null) => {
    // Reset any in-flight book.
    if (abortRef.current) abortRef.current.abort();
    silenceCurrent();
    prefetchRef.current = null;
    runningRef.current = false;
    pausedRef.current = false;
    indexRef.current = 0;
    setProgress(0);
    setCurrentPage(0);
    setError('');
    const isEpub = /\.epub$/i.test(file.name) || file.type === 'application/epub+zip';
    setBookTitle(file.name.replace(/\.(pdf|epub)$/i, ''));
    setStatus('loading');

    try {
      const sentences = [];

      if (isEpub) {
        // EPUB: real paragraphs, walked in spine order. `page` carries the
        // chapter number so the existing progress readout works unchanged.
        const book = await extractEpub(file);
        if (book.title) setBookTitle(book.title);
        setPageCount(book.chapters.length);
        chapterNamesRef.current = book.chapters.map((c) => c.title);
        book.chapters.forEach((chapter, i) => {
          const text = preprocessForTTS(chapter.text);
          for (const line of splitIntoSentences(text)) sentences.push({ text: line, page: i + 1 });
        });
        bookMetaRef.current = { title: book.title, author: book.author };
      } else {
        const pdfjsLib = await loadPdfJs();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        setPageCount(pdf.numPages);
        chapterNamesRef.current = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const content = await page.getTextContent();
          const pageText = preprocessForTTS(content.items.map((it) => it.str).join(' '));
          for (const line of splitIntoSentences(pageText)) sentences.push({ text: line, page: p });
        }
        bookMetaRef.current = { title: file.name.replace(/\.pdf$/i, ''), author: '' };
      }

      if (!sentences.length) {
        setError(isEpub
          ? 'No readable text found in that EPUB.'
          : 'No readable text found — this looks like a scanned PDF (images only).');
        setStatus('error');
        return;
      }

      sentencesRef.current = sentences;

      // Put it on the shelf (idempotent) and pick up where we left off.
      try {
        const record = await shelf.addBook(file, bookMetaRef.current);
        bookIdRef.current = record.id;
        const saved = startIndex != null ? { index: startIndex } : await shelf.getProgress(record.id);
        if (saved && saved.index > 0 && saved.index < sentences.length) {
          indexRef.current = saved.index;
          setProgress(saved.index / sentences.length);
          setCurrentPage(sentences[saved.index]?.page || 0);
        }
        refreshShelf();
      } catch (e) {
        // A full or unavailable IndexedDB must not stop a book from playing.
        console.warn('[Audiobook] shelf unavailable:', e?.message);
      }

      abortRef.current = new AbortController();
      // Unlock/resume audio from this user gesture.
      ensureAudioContext();
      setStatus('reading');
      readLoop();
    } catch (err) {
      console.error('[Audiobook] Failed to open book:', err);
      setError(/Could not load|EPUB/i.test(err.message || '')
        ? err.message
        : (isEpub ? 'Could not read that EPUB.' : 'Could not read that PDF.'));
      setStatus('error');
    }
  }, [silenceCurrent, ensureAudioContext, readLoop]);

  /**
   * Jump whole chapters (or PDF pages). Front matter, blurbs and copyright
   * pages are indistinguishable from real chapters often enough that guessing
   * is worse than letting the listener move.
   */
  const skipChapter = useCallback((delta) => {
    const sentences = sentencesRef.current;
    if (!sentences.length) return;
    const here = sentences[indexRef.current]?.page ?? 1;
    const target = here + delta;
    let idx = sentences.findIndex((x) => x.page === target);
    if (idx < 0) idx = delta > 0 ? sentences.length - 1 : 0;

    silenceCurrent();
    prefetchRef.current = null;
    indexRef.current = idx;
    setCurrentPage(sentences[idx]?.page || 0);
    setProgress(idx / sentences.length);

    // Keep playing if we were playing; a skip should not silently stop the book.
    if (!pausedRef.current) {
      if (!abortRef.current || abortRef.current.signal.aborted) {
        abortRef.current = new AbortController();
      }
      if (!runningRef.current) readLoop();
    }
  }, [silenceCurrent, readLoop]);

  const refreshShelf = useCallback(() => {
    shelf.listBooks().then(setBooks).catch(() => {});
  }, []);

  useEffect(() => { refreshShelf(); }, [refreshShelf]);

  // Persist the position as it reads. The read loop calls setProgress once per
  // sentence, so this fires exactly on advance and the loop needs no changes.
  useEffect(() => {
    if (!bookIdRef.current) return;
    if (status !== 'reading' && status !== 'paused') return;
    const total = sentencesRef.current.length;
    if (!total) return;
    const idx = indexRef.current;
    shelf.saveProgress(bookIdRef.current, {
      index: idx,
      total,
      chapter: sentencesRef.current[idx]?.page || 0,
      chapterTitle: chapterNamesRef.current[(sentencesRef.current[idx]?.page || 1) - 1] || '',
    }).catch(() => {});
  }, [progress, status]);

  /** Reopen a book already on the shelf, resuming where it stopped. */
  const openFromShelf = useCallback(async (id) => {
    const record = await shelf.getBook(id);
    if (!record?.blob) return;
    const file = record.blob instanceof File
      ? record.blob
      : new File([record.blob], record.fileName || `${record.title}.epub`);
    loadAndRead(file);
  }, [loadAndRead]);

  const startOver = useCallback(async (id) => {
    await shelf.resetProgress(id);
    const record = await shelf.getBook(id);
    if (!record?.blob) return;
    const file = record.blob instanceof File
      ? record.blob
      : new File([record.blob], record.fileName || `${record.title}.epub`);
    loadAndRead(file, 0);
  }, [loadAndRead]);

  const removeFromShelf = useCallback(async (id) => {
    await shelf.removeBook(id);
    refreshShelf();
  }, [refreshShelf]);

  const pause = useCallback(() => {
    if (status !== 'reading') return;
    pausedRef.current = true;
    silenceCurrent();
    setStatus('paused');
  }, [status, silenceCurrent]);

  const resume = useCallback(() => {
    if (status !== 'paused') return;
    pausedRef.current = false;
    if (!abortRef.current || abortRef.current.signal.aborted) {
      abortRef.current = new AbortController();
    }
    setStatus('reading');
    readLoop();
  }, [status, readLoop]);

  const stop = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    silenceCurrent();
    prefetchRef.current = null;
    runningRef.current = false;
    pausedRef.current = false;
    indexRef.current = 0;
    sentencesRef.current = [];
    setProgress(0);
    setCurrentPage(0);
    setPageCount(0);
    setBookTitle('');
    setError('');
    setStatus('idle');
  }, [silenceCurrent]);

  return {
    status, bookTitle, pageCount, currentPage, progress, error,
    loadAndRead, pause, resume, stop,
    books, refreshShelf, openFromShelf, startOver, removeFromShelf, skipChapter,
    chapterName: chapterNamesRef.current[(currentPage || 1) - 1] || '',
  };
}
